/**
 * Data Import API — column contracts and downloadable templates.
 *
 * CR-6724: the expected-column lists used to be a hand-typed array inside
 * `DataImport.tsx`, with what the importer actually reads defined separately in
 * the backend. They drifted — columns were advertised that nothing read, and
 * columns the importer required were never shown. Both now come from one
 * backend spec, so the dialog, the template and the importer cannot disagree.
 */
import api from './client';
import type { AxiosResponse } from './client';

/** One column of an import file, as the backend spec defines it. */
export interface ImportTemplateColumn {
  /** Canonical header, written into the generated template. */
  name: string;
  /** The import fails without this column. */
  required: boolean;
  /** One-line explanation of what the column carries. */
  help: string;
  /** Alternative headers the importer also honours. */
  accepts: string[];
  /** Closed set of accepted values, where the column has one. */
  allowed: string[];
  /**
   * Read by the importer but aimed at ERP extracts — documented in the
   * template's Column Guide rather than pre-made on the data sheet.
   */
  advanced: boolean;
}

/** The full column contract for one wizard step. */
export interface ImportTemplateSpec {
  step: number;
  name: string;
  /** Worksheet the importer looks for before falling back to the first sheet. */
  sheet_name: string;
  /** Caveats worth showing alongside the columns. */
  notes: string[];
  /** Mandatory columns first, then optional. */
  columns: ImportTemplateColumn[];
}

interface ImportTemplatesResponse {
  steps: ImportTemplateSpec[];
}

export const dataImportAPI = {
  /** Expected columns for every import step. */
  getTemplates: (): Promise<AxiosResponse<ImportTemplatesResponse>> =>
    api.get('/data-import/sessions/templates/'),

  /** Pre-formatted .xlsx template for one step. */
  downloadTemplate: (step: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/data-import/sessions/template/${step}/`, { responseType: 'blob' }),
};
